import { hasFlag } from '@ofmos/shared/roles';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useObjects } from '../../data/hooks';
import { Avatar, cx, type ObjectIcon } from '../../ui';
import { useLogout, useSession } from '../auth/useSession';
import { objectByName } from '../dashboard/queries';
import {
  SETTINGS_PANE_SEARCH_WORDS,
  SETTINGS_PANES,
  type SettingsPaneSlug,
} from '../nav/settingsStack';
import { NavIcon } from './NavIcon';
import { navItem } from './navItem';

/** A settings pane row: the pane, its symbol and whether this person may see it. */
export interface SettingsPaneRow {
  readonly path: SettingsPaneSlug;
  readonly icon: ObjectIcon;
}

/** What decides which panes a person sees, read off the session and the model. */
export interface SettingsAccess {
  /** The legacy derived role, which still gates the admin panes (`RequireAdmin`). */
  readonly admin: boolean;
  readonly may: (flag: 'IMPORT_CSV' | 'EXPORT_CSV' | 'INTAKE_MANAGE' | 'HEALTH_MANAGE') => boolean;
  /**
   * The bot's pane shows where the `message_template` object does, the gate its welcome messages
   * had (fulfillment P2 item 5; BR-SETTINGS item 2).
   */
  readonly hasTemplates: boolean;
}

/**
 * THE PANES IN SYSTEM SETTINGS ORDER, GROUPED BY SPACE (redesign register A5-5, settings variant
 * B with its two moves: "Order: account block; Dashboard; Users; Welcome messages; Health;
 * Imports; Exports; Objects and fields; Identity; Workflows; Webhooks; Inbound sources; AI
 * assistant; Instance; Sidebar; Appearance; Sign out"). The person's own row (Sidebar), the way
 * back (Dashboard) and Sign out are not panes of the instance and are drawn by the sidebar around
 * this list; these are the instance's panes, in the three groups the variants page draws between
 * Dashboard and Sidebar.
 *
 * TWO ROWS CHANGED ON 2026-10-05. The bot's pane, titled by the configuration, stands where
 * Welcome messages stood and holds them as its first section (register A7-S4, A8-3: one
 * destination per thing). Appearance left the list: its settings are submenus of the account
 * menu (A8-7), so the person's own rows are Sidebar, then Sign out.
 *
 * VISIBILITY IS EXACTLY WHAT IT WAS: admin for the admin panes, a flag for the four flag-gated
 * ones, the object for the bot's pane as it was for Welcome messages. A group whose rows a person
 * may not see is not drawn, and the `query` keeps only the rows whose title, or the title of one
 * of their sections (`SETTINGS_PANE_SEARCH_WORDS`), holds it.
 */
export function settingsPaneGroups(
  access: SettingsAccess,
  query = '',
): readonly (readonly SettingsPaneRow[])[] {
  const groups: readonly (readonly (SettingsPaneRow & { readonly visible: boolean })[])[] = [
    [
      { path: 'members', icon: 'key', visible: access.admin },
      { path: 'bot', icon: 'bot', visible: access.hasTemplates },
      { path: 'health', icon: 'health', visible: access.may('HEALTH_MANAGE') },
    ],
    [
      { path: 'imports', icon: 'imports', visible: access.may('IMPORT_CSV') },
      { path: 'exports', icon: 'exports', visible: access.may('EXPORT_CSV') },
    ],
    [
      { path: 'objects', icon: 'objects', visible: true },
      { path: 'identity', icon: 'identity', visible: access.admin },
      { path: 'workflows', icon: 'workflows', visible: access.admin },
      { path: 'webhooks', icon: 'webhooks', visible: access.admin },
      { path: 'inbound', icon: 'inbound', visible: access.may('INTAKE_MANAGE') },
      { path: 'agent', icon: 'ai', visible: access.admin },
      { path: 'instance', icon: 'instance', visible: access.admin },
    ],
  ];
  const needle = query.trim().toLowerCase();
  return groups
    .map((group) =>
      group
        .filter(
          (row) =>
            row.visible &&
            [SETTINGS_PANES[row.path], ...(SETTINGS_PANE_SEARCH_WORDS[row.path] ?? [])].some(
              (words) => words.toLowerCase().includes(needle),
            ),
        )
        .map(({ path, icon }) => ({ path, icon })),
    )
    .filter((group) => group.length > 0);
}

/**
 * S1-6: the settings list, one pane per row, under the person's own account block.
 *
 * THE TILE COLOUR IS THE SYMBOL'S, and no row states one: Athena's `color: 'purple'` hid the
 * assistant's own Siri tile, and the owner decided on 2026-09-17 for the tile Apple draws
 * (ledger A5-3 supersedes S1-11; review check (a8) holds it).
 *
 * THE ROW LABELS ARE THE PANE TITLES, AND THERE IS ONE LIST OF THEM (round 3, SH-A8):
 * `app/nav/settingsStack.ts` owns the words; this file owns which rows a person may see, in which
 * group, with which symbol.
 *
 * `leading` and `trailing` are groups the desktop sidebar adds around the panes (the way back to
 * the Dashboard first, the person's own rows and Sign out last, A5-5); the phone's More sheet
 * passes neither and draws its own rows under the list (P6).
 */
export function SettingsNavigation({
  query = '',
  onNavigate,
  activePath,
  leading,
  trailing,
}: {
  readonly query?: string;
  readonly onNavigate?: () => void;
  readonly activePath?: string | undefined;
  readonly leading?: ReactNode;
  readonly trailing?: ReactNode;
}) {
  const session = useSession();
  const person = session.data;
  const permissions = person?.permissions;
  const hasTemplates = objectByName(useObjects().data, 'message_template') !== undefined;
  const visibleGroups = settingsPaneGroups(
    {
      admin: person?.role === 'admin',
      may: (flag) => permissions !== undefined && hasFlag(permissions, flag),
      hasTemplates,
    },
    query,
  );

  return (
    <div className="settings-navigation">
      <div className="shell-account">
        <Avatar name={person?.name ?? 'Signed out'} size="sm" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{person?.name ?? 'Signed out'}</p>
          <p className="truncate text-2xs text-ink-muted">{person?.email ?? 'Not signed in'}</p>
        </div>
      </div>
      <nav aria-label="Settings" className="shell-settings-groups">
        {leading}
        {visibleGroups.map((group) => (
          <ul key={group[0]?.path}>
            {group.map((row) => (
              <li key={row.path}>
                <NavLink
                  to={`/settings/${row.path}`}
                  className={({ isActive }) =>
                    navItem({
                      isActive:
                        activePath === undefined
                          ? isActive
                          : activePath === `/settings/${row.path}`,
                    })
                  }
                  onClick={onNavigate}
                  aria-current={activePath === `/settings/${row.path}` ? 'page' : undefined}
                >
                  <NavIcon icon={row.icon} />
                  <span className="truncate">{SETTINGS_PANES[row.path]}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        ))}
        {visibleGroups.length === 0 && (
          <p className="px-3 text-sm text-ink-muted">No settings match.</p>
        )}
        {trailing}
      </nav>
    </div>
  );
}

/**
 * THE PERSON'S OWN SETTINGS STAND AT THE BOTTOM (fulfillment round 2, F3; register A5-5). The
 * Sidebar pane is about the person and not the instance, so it stands at the foot of the settings
 * list on the desktop and in the phone's More sheet (`MobileNav.tsx`); Appearance stood beside it
 * until its settings moved into the account menu (A8-7). `sidebar` is the existing symbol drawn
 * for that word.
 */
export function PersonalSettingsRow({ onNavigate }: { readonly onNavigate?: () => void }) {
  return (
    <NavLink to="/settings/sidebar" className={navItem} onClick={onNavigate}>
      <NavIcon icon="sidebar" />
      <span className="truncate">{SETTINGS_PANES.sidebar}</span>
    </NavLink>
  );
}

/**
 * THE WAY OUT, LAST (A5-5: "...; Sidebar; Appearance; Sign out"). The existing logout, which
 * ends the session in this tab even when the request fails, then the login screen. The symbol is
 * our drawing of Apple's `rectangle.portrait.and.arrow.right` (register A5-2).
 */
export function SignOutRow() {
  const logout = useLogout();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      disabled={logout.isPending}
      /* The pill is the row's box, not the parent's content box (package A4). */
      className={cx(navItem({ isActive: false }), 'disabled:opacity-50')}
      onClick={() =>
        logout.mutate(undefined, {
          onSettled: () => void navigate('/login', { replace: true }),
        })
      }
    >
      <NavIcon icon="rectangle-portrait-and-arrow-right" />
      <span>Sign out</span>
    </button>
  );
}
